import { useEffect, useState, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { Container, Row, Col } from 'react-bootstrap';
import { fetchConfigs } from '../../../../redux/slices/configSlice';
import SearchBox from 'components/common/SearchBox';
import { getActions, ActionButton, type ActionEntity } from 'actions';

const toActionEntity = (config: any): ActionEntity => ({ type: 'config_item', id: Number(config.config_id), key: config.key });

const ListConfig = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const configs = useAppSelector((state) => state.config.items);

  const [selectedConfigIds, setSelectedConfigIds] = useState<Set<number>>(new Set());
  const [filteredConfigs, setFilteredConfigs] = useState(configs);

  // Fetch all configs on component mount
  useEffect(() => {
    dispatch(fetchConfigs());
  }, [dispatch]);

  useEffect(() => {
    setFilteredConfigs(configs);
  }, [configs]);

  const refresh = () => {
    dispatch(fetchConfigs());
  };

  // Handle search input change
  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;

    if (term.length > 0) {
      setFilteredConfigs(configs.filter((config: any) => config.key.toLowerCase().includes(term.toLowerCase())));
    } else {
      setFilteredConfigs(configs);
    }
  };

  const toggleConfigSelection = (configId: number) => {
    setSelectedConfigIds((prev) => {
      const next = new Set(prev);
      if (next.has(configId)) next.delete(configId);
      else next.add(configId);
      return next;
    });
  };

  const selectionEntities = Array.from(selectedConfigIds)
    .map((id) => configs.find((c: any) => Number(c.config_id) === id))
    .filter(Boolean)
    .map((c) => toActionEntity(c));
  const toolbarActions = getActions({ entities: selectionEntities, refresh });

  return (
    <Container fluid className="p-0">
      {/* Search bar and New button */}
      <Row className="m-0 mb-4">
        <Col className="p-0 d-flex justify-content-between align-items-center">
          <SearchBox placeholder={t('search') + '...'} size="sm" onChange={handleSearch} />
          <div className="d-flex gap-2">
            <ActionButton actions={toolbarActions} action="addToTransportConfigItem" variant="secondary" size="sm" label="Add selected to transport" />
            <ActionButton actions={toolbarActions} action="createConfigItem" variant="outline-primary" size="sm" />
          </div>
        </Col>
      </Row>

      <Row className="m-0">
        <Col className="p-0">
          {/* Table */}
          <table className="table table-hover table-sm">
            <thead>
              <tr>
                <th />
                <th>#</th>
                <th>{t('key')}</th>
                <th>{t('action')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredConfigs.map((config: any, i: number) => {
                const rowActions = getActions({ entities: [toActionEntity(config)], refresh });
                return (
                  <tr key={config.config_id ?? i}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedConfigIds.has(Number(config.config_id))}
                        onChange={() => toggleConfigSelection(Number(config.config_id))}
                      />
                    </td>
                    <td>{i + 1}</td>
                    <td>{config.key}</td>
                    <td>
                      <ActionButton actions={rowActions} action="addToTransportConfigItem" variant="outline-secondary" size="sm" />{' '}
                      <ActionButton actions={rowActions} action="edit" variant="outline-secondary" />{' '}
                      <ActionButton actions={rowActions} action="delete" variant="outline-danger" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Col>
      </Row>
    </Container>
  );
};

export default ListConfig;
